import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useSitesStore, generateSiteId } from '../../store/sitesStore';
import { TowerShape, StayWire, Site, Tower } from '../../types/site';
import { calculateMountingPoints, normDegrees as norm } from '../../lib/towerUtils';

interface SiteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SiteModal: React.FC<SiteModalProps> = ({ isOpen, onClose }) => {
  const [siteName, setSiteName] = useState('');
  const [location, setLocation] = useState('');
  const [sections, setSections] = useState(10); // Number of sections
  const [sectionHeight, setSectionHeight] = useState(3);
  const [towerShape, setTowerShape] = useState<TowerShape>('square');
  const [stayWires, setStayWires] = useState<Array<{ height: number; distance: number }>>([]);
  const { currentProject, appTheme } = useProjectStore();
  const { createSite } = useSitesStore();
  const isDarkTheme = appTheme === 'dark';
  const [orientationDeg, setOrientationDeg] = useState(0);

  if (!isOpen) return null;

  // Check if a collection is loaded
  if (!currentProject) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">No Collection Loaded</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-md">
              <X size={20} />
            </button>
          </div>
          <p className="text-gray-600 mb-4">Please load or create a collection before creating sites.</p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Calculate tower height from sections
  const towerHeight = sections * sectionHeight;

  const handleClose = () => {
    onClose();
    setSiteName('');
    setLocation('');
    setSections(10);
    setSectionHeight(3);
    setTowerShape('square');
    setStayWires([]);
  };

  const generateId = () => generateSiteId();

  const addStayWire = () => {
    setStayWires([...stayWires, { height: towerHeight / 2, distance: 10 }]);
  };

  const removeStayWire = (index: number) => {
    setStayWires(stayWires.filter((_, i) => i !== index));
  };

  const updateStayWire = (index: number, field: 'height' | 'distance', value: number) => {
    const updated = [...stayWires];
    updated[index][field] = value;
    setStayWires(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (siteName && location && currentProject) {
      // Convert stayWires to full StayWire objects with angles
      const numSides = towerShape === 'triangle' ? 3 : 4;
      const fullStayWires: StayWire[] = stayWires.flatMap(wire => {
        // Create stay wires at each corner of the tower
        const wires: StayWire[] = [];
        for (let i = 0; i < numSides; i++) {
          const angle = towerShape === 'triangle' 
            ? i * 120  // 0°, 120°, 240°
            : i * 90;  // 0°, 90°, 180°, 270°
          wires.push({
            id: generateId(),
            height: wire.height,
            distance: wire.distance,
            angle
          });
        }
        return wires;
      });
      
      // Create tower
      const tower: Tower = {
        id: generateId(),
        height: towerHeight,
        crossMemberWidth: 450,
        sectionHeight,
        shape: towerShape,
        mountingPoints: calculateMountingPoints(towerHeight, towerShape),
        stayWires: fullStayWires,
        orientationDeg: norm(orientationDeg)
      };

      // Create site
      const site: Site = {
        id: generateId(),
        name: siteName,
        location,
        tower,
        equipment: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await createSite(site);
      handleClose();
    }
  };

  const inputClass = `w-full px-3 py-2 border rounded-md focus:outline-none ${
    isDarkTheme
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-gray-500'
      : 'border-gray-300 focus:border-blue-500'
  }`;

  const compactInputClass = `w-full px-2 py-1 text-sm border rounded focus:outline-none ${
    isDarkTheme
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-gray-500'
      : 'border-gray-300 focus:border-blue-500'
  }`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[min(90vh,100%)] flex flex-col overflow-hidden">
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Create New Site</h2>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-md">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto modal-scroll">
            <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site Name
            </label>
            <input
              type="text"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              className={inputClass}
              placeholder="Enter site name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className={inputClass}
              placeholder="Enter location"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tower Shape
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTowerShape('square')}
                className={`px-4 py-2 border-2 rounded-md transition-colors ${
                  towerShape === 'square'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Square
              </button>
              <button
                type="button"
                onClick={() => setTowerShape('triangle')}
                className={`px-4 py-2 border-2 rounded-md transition-colors ${
                  towerShape === 'triangle'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Triangle
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Sections
            </label>
            <input
              type="number"
              value={sections}
              onChange={(e) => setSections(Number(e.target.value))}
              className={inputClass}
              min="1"
              max="100"
            />
            <p className="text-xs text-gray-500 mt-1">
              Tower height: <span className="text-gray-600 font-medium">{towerHeight}m</span> ({sections} sections × {sectionHeight}m)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tower Orientation (°)</label>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min={0}
                max={360}
                value={orientationDeg}
                onChange={(e) => setOrientationDeg(Math.max(0, Math.min(360, Number(e.target.value) || 0)))}
                className={`w-24 px-3 py-2 border rounded-md focus:outline-none ${
                  isDarkTheme
                    ? 'bg-gray-700 border-gray-600 text-white focus:border-gray-500'
                    : 'border-gray-300 focus:border-blue-500'
                }`}
              />
              <input
                type="range"
                min={0}
                max={360}
                value={orientationDeg}
                onChange={(e) => setOrientationDeg(Number(e.target.value))}
                className="flex-1"
              />
              <div className="w-12 h-12 rounded-full bg-gray-100 border flex items-center justify-center relative">
                <div
                  className="w-0 h-0 border-l-4 border-r-4 border-b-[12px] border-l-transparent border-r-transparent border-b-blue-600 absolute"
                  style={{ transform: `rotate(${orientationDeg}deg) translateY(-2px)` }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Sets the tower heading (top-down). Equipment azimuths reflect this.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Section Height (meters)
            </label>
            <input
              type="number"
              value={sectionHeight}
              onChange={(e) => setSectionHeight(Number(e.target.value))}
              className={inputClass}
              min="1"
              max="10"
              step="0.5"
            />
            <p className="text-xs text-gray-500 mt-1">
              Standard towers use 3m sections. Adjust as needed for your design.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Stay Wires (Optional)
              </label>
              <button
                type="button"
                onClick={addStayWire}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus size={16} />
                <span>Add Stay Wire</span>
              </button>
            </div>
            {stayWires.length > 0 && (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {stayWires.map((wire, index) => (
                  <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-md">
                    <div className="flex-1">
                      <input
                        type="number"
                        value={wire.height}
                        onChange={(e) => updateStayWire(index, 'height', Number(e.target.value))}
                        className={compactInputClass}
                        placeholder="Height (m)"
                        min="0"
                        max={towerHeight}
                        step="0.5"
                      />
                      <p className="text-xs text-gray-500 mt-0.5">Height (m)</p>
                    </div>
                    <div className="flex-1">
                      <input
                        type="number"
                        value={wire.distance}
                        onChange={(e) => updateStayWire(index, 'distance', Number(e.target.value))}
                        className={compactInputClass}
                        placeholder="Distance (m)"
                        min="1"
                        max="50"
                        step="0.5"
                      />
                      <p className="text-xs text-gray-500 mt-0.5">Distance (m)</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeStayWire(index)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Stay wires will be added at each corner of the tower at the specified height.
            </p>
          </div>
            </div>
          </div>

          <div className="flex-shrink-0 flex space-x-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create Site
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SiteModal;
